import { api } from "lib/graphql/api"
import { gql } from "@apollo/client"
import { query } from "lib/graphql/query"
import { mutate } from "lib/graphql/mutate"

/**
 * Fragment of the fields exposed by the server `ShareLinkFieldResponse` type
 * (see `routes/sharing/server/sharing.server.mjs`). Shared across the read queries
 * and the save mutation so callers always receive the same row shape.
 */
const SHARE_LINK_FIELD_RESPONSE_FIELDS = gql`
    fragment ShareLinkFieldResponseFields on ShareLinkFieldResponse {
        _id
        linkId
        regimeId
        regimeVersion
        values
        authorScope
        createdBy
        authorName
        authorEmail
        sourceClient
        recipientClient
        created
        modified
    }
`

/**
 * Create or update the current viewer's field response for a share link / regime.
 * Mirrors the server mutation `saveShareLinkFieldResponse(input: SaveShareLinkFieldResponseInput!)`.
 *
 * @param {{ shareLinkId: string, regimeId: string, values: object }} input
 * @returns {Promise<object>} the saved `ShareLinkFieldResponse` row
 */
export const saveShareLinkFieldResponse = api(async function saveShareLinkFieldResponse(input) {
    return mutate(
        gql`
            mutation SaveShareLinkFieldResponse($input: SaveShareLinkFieldResponseInput!) {
                saveShareLinkFieldResponse(input: $input) {
                    ...ShareLinkFieldResponseFields
                }
            }
            ${SHARE_LINK_FIELD_RESPONSE_FIELDS}
        `,
        { input },
        { returns: "saveShareLinkFieldResponse" }
    )
})

/**
 * List every visible field response for a share link / regime (owner/internal view).
 * Mirrors the server query `listShareLinkFieldResponses(shareLinkId: String!, regimeId: String!)`,
 * which returns a `ShareLinkFieldResponseResult` ({ total, responses }).
 *
 * Usage: `listShareLinkFieldResponses.useResults({ shareLinkId, regimeId })`.
 *
 * @param {{ shareLinkId: string, regimeId: string }} args
 * @returns {Promise<{ total: number, responses: object[] }>}
 */
export const listShareLinkFieldResponses = api(async function listShareLinkFieldResponses({ shareLinkId, regimeId }) {
    if (!shareLinkId || !regimeId) return { total: 0, responses: [] }
    return query(
        gql`
            query ListShareLinkFieldResponses($shareLinkId: String!, $regimeId: String!) {
                listShareLinkFieldResponses(shareLinkId: $shareLinkId, regimeId: $regimeId) {
                    total
                    responses {
                        ...ShareLinkFieldResponseFields
                    }
                }
            }
            ${SHARE_LINK_FIELD_RESPONSE_FIELDS}
        `,
        { shareLinkId, regimeId },
        { returns: "listShareLinkFieldResponses" }
    )
})

/**
 * Load the current viewer's own field response for a share link / regime (recipient panel).
 * Mirrors the server query `getShareLinkFieldResponseForViewer(shareLinkId: String!, regimeId: String!)`,
 * which returns a single `ShareLinkFieldResponse` (or null when none exists yet).
 *
 * Usage: `getShareLinkFieldResponseForViewer.useResults({ shareLinkId, regimeId })` or `.useResultsOnce(...)`.
 *
 * @param {{ shareLinkId: string, regimeId: string }} args
 * @returns {Promise<object|null>}
 */
export const getShareLinkFieldResponseForViewer = api(async function getShareLinkFieldResponseForViewer({
    shareLinkId,
    regimeId,
}) {
    if (!shareLinkId || !regimeId) return null
    return query(
        gql`
            query GetShareLinkFieldResponseForViewer($shareLinkId: String!, $regimeId: String!) {
                getShareLinkFieldResponseForViewer(shareLinkId: $shareLinkId, regimeId: $regimeId) {
                    ...ShareLinkFieldResponseFields
                }
            }
            ${SHARE_LINK_FIELD_RESPONSE_FIELDS}
        `,
        { shareLinkId, regimeId },
        { returns: "getShareLinkFieldResponseForViewer" }
    )
})

/**
 * Regime-level aggregate of every share-link field response across all links in a regime, grouped
 * by share link, for internal reviewers (the B6 responses regime tab). Mirrors the server query
 * `listShareLinkFieldResponsesForRegime(regimeId: String!)`, which returns a
 * `ShareLinkFieldResponseRegimeResult` ({ groups, total }). The server enforces EDITOR-ONLY access
 * and multi-tenant isolation; recipients are rejected.
 *
 * Each group carries the link's `requestedFields` so the UI can render every value by type.
 *
 * Usage: `listShareLinkFieldResponsesForRegime.useResults({ regimeId })`.
 *
 * @param {{ regimeId: string }} args
 * @returns {Promise<{ groups: object[], total: number }>}
 */
export const listShareLinkFieldResponsesForRegime = api(async function listShareLinkFieldResponsesForRegime({
    regimeId,
}) {
    if (!regimeId) return { total: 0, groups: [] }
    return query(
        gql`
            query ListShareLinkFieldResponsesForRegime($regimeId: String!) {
                listShareLinkFieldResponsesForRegime(regimeId: $regimeId) {
                    total
                    groups {
                        shareLinkId
                        shareLinkName
                        requestedFields {
                            id
                            type
                            label
                            required
                            options
                            order
                            currencyCode
                        }
                        responses {
                            ...ShareLinkFieldResponseFields
                        }
                    }
                }
            }
            ${SHARE_LINK_FIELD_RESPONSE_FIELDS}
        `,
        { regimeId },
        { returns: "listShareLinkFieldResponsesForRegime" }
    )
})
